import { mergeSocialSourcesForBlog } from "@/platforms/customer/services/blogService";
import React, { useMemo } from "react";
import { FaFacebookF, FaInstagram, FaLinkedinIn } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { FiGlobe } from "react-icons/fi";

export const useBlogDetailSocials = (
  ensureHttpHref,
  isJunkOneLetterHost,
  blog,
  rawBlogForSocial,
) => {
  const socials = useMemo(() => {
    const fromUi = blog?.socialLinks;
    const hasUi =
      fromUi &&
      typeof fromUi === "object" &&
      !Array.isArray(fromUi) &&
      Object.values(fromUi).some((v) => v != null && String(v).trim() !== "");
    if (!rawBlogForSocial) {
      return hasUi ? fromUi : {};
    }
    const fromApi = mergeSocialSourcesForBlog(rawBlogForSocial);
    const hasApi = Object.values(fromApi).some(
      (v) => v != null && String(v).trim() !== "",
    );
    if (hasApi && !hasUi) return fromApi;
    if (hasUi) return { ...fromApi, ...fromUi };
    return hasApi ? fromApi : fromUi || {};
  }, [blog?.socialLinks, rawBlogForSocial]);

  const socialEntries = useMemo(() => {
    // Per network: do not dedupe by URL across platforms — same "link in bio" in every field is common; old
    // logic only kept the first slot (Instagram) and hid the rest.
    // Order: Instagram, LinkedIn, Facebook, X, website.
    const primary = [
      {
        key: "instagram",
        val: String(socials.instagram || "").trim(),
        label: "Instagram",
        Icon: FaInstagram,
      },
      {
        key: "linkedin",
        val: String(socials.linkedin || "").trim(),
        label: "LinkedIn",
        Icon: FaLinkedinIn,
      },
      {
        key: "facebook",
        val: String(socials.fb || socials.facebook || "").trim(),
        label: "Facebook",
        Icon: FaFacebookF,
      },
      {
        key: "twitter",
        val: String(socials.twitter || socials.x || "").trim(),
        label: "X",
        Icon: FaXTwitter,
      },
      {
        key: "website",
        val: String(socials.website || socials.url || "").trim(),
        label: "Website",
        Icon: FiGlobe,
      },
    ];
    const out = [];
    const hrefsFromPrimary = new Set();
    for (const p of primary) {
      const href = ensureHttpHref(p.val);
      if (!href || isJunkOneLetterHost(href)) continue;
      out.push({ key: p.key, href, label: p.label, Icon: p.Icon });
      hrefsFromPrimary.add(href.toLowerCase());
    }
    const alreadyHandledKey = new Set(
      "instagram ig insta linkedin linked-in facebook fb twitter x website url link links".split(
        " ",
      ),
    );
    for (const [k, v] of Object.entries(socials)) {
      if (v == null) continue;
      if (typeof v === "object" && !Array.isArray(v)) continue;
      const raw = String(v).trim();
      if (!raw) continue;
      const href = ensureHttpHref(raw);
      if (!href || isJunkOneLetterHost(href)) continue;
      const n = href.toLowerCase();
      if (hrefsFromPrimary.has(n)) continue;
      if (alreadyHandledKey.has(String(k).toLowerCase())) continue;
      hrefsFromPrimary.add(n);
      out.push({ key: `extra-${k}`, href, label: String(k), Icon: FiGlobe });
    }
    return out;
  }, [socials]);
  return { socialEntries };
};
